/**
 * This higher-order component (container) contains the KCE Database Tools 2 application.
 * It is responsible of the top-most menu on the page and selecting the other
 * components on the page on the basis of the selection in the top-most menu.
 *
 * This file is also rigorously commented to help to understand the application.
 */

import * as React from "react";
import { BrowserRouter as Router, Route, Redirect, Switch } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Dimmer, Loader } from "semantic-ui-react";
import { isAdmin, hasRole } from "../../utils/utils";
import { PdApiActions, pdApiActions } from "../../actions/pdApiActions";
import { MainActions, mainActions } from "../../actions/mainActions";
import { EditActions, editActions } from "../../actions/editActions";
import PageTitle from "../../components/PageTitle/PageTitle";
import SideMenu from "../../components/SideMenu/SideMenu";
import Lift from "../Lift/Lift";
import Group from "../Group/Group";
import APM from "../APM/APM";
import Drive from "../Drive/Drive";
import Signalization from "../Signalization/Signalization";
import Users from "../../containers/Users/Users";
import NotificationOverlay from "./../NotificationOverlay/NotificationOverlay";
import styles from "./Main.module.scss";
import ChangeLog from "../../components/ChangeLog/ChangeLog";
import Enumerations from "../../containers/Enumerations/Enumerations";
import ErrorPage from "../../components/ErrorPage/ErrorPage";
import UnderConstruction from "../../components/UnderConstruction/UnderConstruction";
import Safety from "../Safety/Safety";
import Escalator from "../Escalator/Escalator";
import Door from "../Door/Door";
import NonKone from "../NonKone/NonKone";
import Network from "../Network/Network";
import Monitoring from "../Monitoring/Monitoring";
import Tools from "../Tools/Tools";
import Localization from "../Localization/Localization";
import { AuthProvider } from "../../components/AuthProvider/AuthProvider";
/**
 * Properties or props are variables used in react. These props affect the state (the model) of this file (main.tsx).
 *
 * TODO: Why is fetchingStatus not defined by the initial state like activeSubmenu?
 */
type StateProps = {
  fetchingStatus: string | undefined;
  activeSubmenu: State.Submenu;
  activeUser: Api.User | undefined;
};

/**
 * These props represent the possible actions started from this file. Both user actions and lifecycle actions.
 */
type DispatchProps = {
  pdApiActions: typeof pdApiActions;
  mainActions: typeof mainActions;
  editActions: typeof editActions;
};

/**
 * If one would need props not related to redux and the common state they could be defined in OwnProps.
 * Such props could be something related purely to the components (the view). Temporary values could be defined in MainState.
 * type OwnProps = {};
 * type MainState = {};
 */

// There is only one props so state and action props are allways combined. (if OwnProps was used, it would also be added here)
export type MainProps = StateProps & DispatchProps;

/**
 * Main component of the application (if MainState was used, if would be added here).  Main will be used in index.tsx with <Main/>
 */
export class Main extends React.Component<MainProps> {
  // Lifecycle method of React which will be called once when component is mounted.
  // TODO: Just checking: lifecycle methods are called every time something happens, right?
  // When does mounting happen? Does it happen if the user changes from "faults" to "users" in the UI?

  componentDidMount(): void {
    this.props.pdApiActions.fetchData();
  }

  // Lifecycle method of React, which every visible component must implement.
  // The function must return single JSX element
  render(): JSX.Element {
    const { fetchingStatus, activeSubmenu } = this.props;
    const { activeUser } = this.props;

    return (
      <Router basename={process.env.PUBLIC_URL}>
        <div className={styles.container}>
          <NotificationOverlay />
          {fetchingStatus && (
            <Dimmer active={true} inverted={true}>
              <Loader content={fetchingStatus} />
            </Dimmer>
          )}
          {/* Check out conditional rendering
          https://reactjs.org/docs/conditional-rendering.html#inline-if-with-logical--operator */}
          {activeUser && !fetchingStatus && (
            <>
              {/* If this syntax seems weird check out a great post by Tyler McGinnis
              https://tylermcginnis.com/react-router-pass-props-to-components/
              TLDR; Component would get remounted every render but we can avoid it by using the render method.
              */}
              <Route
                render={props => (
                  <div className={styles.wrapper}>
                    <PageTitle
                      {...props}
                      activeSubmenu={activeSubmenu}
                      handleSubmenuSelection={this.props.mainActions.setActiveSubmenu}
                      handleReloadSelected={this.handleReload}
                      activeUser={activeUser}
                    />
                    <div className={`${styles.mainContent} mainContent`}>
                      <SideMenu
                        {...props}
                        activeSubmenu={activeSubmenu}
                        activeUser={activeUser}
                        handleNavLinkOnClick={this.props.editActions.cancelEditing}
                      />
                      <div className={`${styles.dynamicContent} dynamicContent`}>
                        <Switch>
                          <Route path="/home/" component={ChangeLog} />
                          <Route path="/lio/" component={UnderConstruction} />
                          <Route path="/profile/" component={UnderConstruction} />
                          <Route path="/setting/" component={UnderConstruction} />
                          <Route path="/site/" component={UnderConstruction} />
                          <Route
                            exact={true}
                            path="/elevator/event-enumerations/"
                            render={() => (
                              <Redirect to="/elevator/event-enumerations/event-classes/" />
                            )}
                          />
                          <Route
                            exact={true}
                            path="/elevator/io-enumerations/"
                            render={() => (
                              <Redirect to="/elevator/io-enumerations/io-board-types/" />
                            )}
                          />
                          <Route
                            exact={true}
                            path="/elevator/bindings/"
                            render={() => <Redirect to="/elevator/bindings/add-bindings/" />}
                          />
                          <Route
                            exact={true}
                            path="/drive/bindings/"
                            render={() => <Redirect to="/drive/bindings/add-bindings/" />}
                          />
                          <Route
                            exact={true}
                            path="/escalator/bindings/"
                            render={() => <Redirect to="/escalator/bindings/add-bindings/" />}
                          />
                          <Route
                            exact={true}
                            path="/door/event-enumerations/"
                            render={() => (
                              <Redirect to="/door/event-enumerations/door-event-classes/" />
                            )}
                          />
                          <Route
                            path="/enumerations/domains/"
                            render={props => <Enumerations {...props} subMenuRoute="Domains" />}
                          />
                          <Route
                            path="/enumerations/electrifications/"
                            render={props => (
                              <Enumerations {...props} subMenuRoute="Electrifications" />
                            )}
                          />

                          <Route
                            path="/enumerations/parameter-categories/"
                            render={props => (
                              <Enumerations {...props} subMenuRoute="Parameter Categories" />
                            )}
                          />
                          <Route
                            path="/enumerations/fault-components/"
                            render={props => (
                              <Enumerations {...props} subMenuRoute="Fault Components" />
                            )}
                          />
                          <Route
                            path="/group/faults/"
                            render={props => <Group {...props} subMenuRoute="Group Faults" />}
                          />
                          <Route
                            path="/group/parameters/"
                            render={props => <Group {...props} subMenuRoute="Group Parameters" />}
                          />
                          <Route
                            path="/group/parameter-categories/"
                            render={props => (
                              <Group {...props} subMenuRoute="Parameter Categories" />
                            )}
                          />
                          <Route
                            path="/elevator/faults/"
                            render={props => <Lift {...props} subMenuRoute="Elevator Faults" />}
                          />
                          <Route
                            path="/elevator/parameters/"
                            render={props => <Lift {...props} subMenuRoute="Elevator Parameters" />}
                          />
                           <Route
                            path="/elevator/data-acts/"
                            render={props => <Lift {...props} subMenuRoute="Data Act" />}
                          />
                          <Route
                            path="/elevator/bindings/:activeMenu?"
                            render={props => <Lift {...props} subMenuRoute="Elevator Bindings" />}
                          />
                          <Route
                            path="/elevator/event-enumerations/:activeMenu?"
                            render={props => <Lift {...props} subMenuRoute="Event Enumerations" />}
                          />
                          <Route
                            path="/elevator/io-enumerations/:activeMenu?"
                            render={props => <Lift {...props} subMenuRoute="IO Enumerations" />}
                          />
                          <Route
                            path="/elevator/parameter-categories/"
                            render={props => (
                              <Lift {...props} subMenuRoute="Parameter Categories" />
                            )}
                          />
                          <Route
                            path="/elevator/fault-components/"
                            render={props => (
                              <Lift {...props} subMenuRoute="Fault Components" />
                            )}
                          />
                          <Route
                            path="/elevator/grand-norms/"
                            render={props => (
                              <Lift {...props} subMenuRoute="Grand Norm" />
                            )}
                          />
                          <Route
                            path="/drive/faults/"
                            render={props => <Drive {...props} subMenuRoute="Drive Faults" />}
                          />
                          <Route
                            path="/drive/parameters/"
                            render={props => <Drive {...props} subMenuRoute="Drive Parameters" />}
                          />
                          <Route
                            path="/drive/parameter-categories/"
                            render={props => (
                              <Drive {...props} subMenuRoute="Parameter Categories" />
                            )}
                          />
                          <Route
                            path="/apm/parameters/"
                            render={props => <APM {...props} subMenuRoute="APM Parameters" />}
                          />                          
                          <Route
                            path="/apm/faults/"
                            render={props => <APM {...props} subMenuRoute="APM Faults" />}
                          />
                          <Route
                            path="/drive/bindings/:activeMenu?"
                            render={props => <Drive {...props} subMenuRoute="Drive Bindings" />}
                          />
                          <Route
                            path="/drive/signals/"
                            render={props => <Drive {...props} subMenuRoute="Drive Signals" />}
                          />
                          <Route
                            path="/drive/tests/"
                            render={props => <Drive {...props} subMenuRoute="Drive Tests" />}
                          />
                          <Route
                            path="/drive/signal-groups/"
                            render={props => (
                              <Drive {...props} subMenuRoute="Drive Signal Groups" />
                            )}
                          />
                          <Route
                            path="/safety/parameters/"
                            render={props => <Safety {...props} subMenuRoute="Safety Parameters" />}
                          />
                          <Route
                            path="/escalator/faults/"
                            render={props => (
                              <Escalator {...props} subMenuRoute="Escalator Faults" />
                            )}
                          />
                          <Route
                            path="/escalator/parameters/"
                            render={props => (
                              <Escalator {...props} subMenuRoute="Escalator Parameters" />
                            )}
                          />
                          <Route
                            path="/escalator/bindings/:activeMenu?"
                            render={props => <Escalator {...props} subMenuRoute="Escalator Bindings" />}
                          />
                          <Route
                            path="/escalator/event-enumerations/:activeMenu?"
                            render={props => (
                              <Escalator {...props} subMenuRoute="Event Enumerations" />
                            )}
                          />
                          <Route
                            path="/escalator/RTDs/"
                            render={props => (
                            <Escalator {...props} subMenuRoute="Escalator RTDs" />
                            )}
                          />
                          <Route
                            path="/escalator/RTD-Groups/"
                            render={props => (
                              <Escalator {...props} subMenuRoute="Escalator RTD Groups" />
                            )}
                          />
                           <Route
                            path="/escalator/data-acts/"
                            render={props => <Escalator {...props} subMenuRoute="Data Acts" />}
                          />
                          <Route
                            path="/escalator/development-packages/"
                            render={props => (
                              <Escalator {...props} subMenuRoute="Development Package" />
                            )}
                          />
                          <Route
                            path="/escalator/event-logs/"
                            render={props => <Escalator {...props} subMenuRoute="Event Logs" />}
                          />
                          <Route
                            path="/door/faults/"
                            render={props => <Door {...props} subMenuRoute="Door Faults" />}
                          />
                          <Route
                            path="/door/parameters/"
                            render={props => <Door {...props} subMenuRoute="Door Parameters" />}
                          />
                          <Route
                            path="/door/bindings/:activeMenu?"
                            render={props => <Door {...props} subMenuRoute="Door Bindings" />}
                          />
                          <Route
                            path="/door/event-enumerations/:activeMenu?"
                            render={props => <Door {...props} subMenuRoute="Event Enumerations" />}
                          />
                          <Route
                            path="/nonkone/faults/"
                            render={props => <NonKone {...props} subMenuRoute="NonKone Faults" />}
                          />
                          <Route
                            path="/signalization/faults/"
                            render={props => <Signalization {...props} subMenuRoute="Signalization Faults" />}/>
                            <Route
                            path="/network/faults/"
                            render={props => <Network {...props} subMenuRoute="Network Faults" />}
                          />
                          <Route
                            path="/monitoring/faults/"
                            render={props => <Monitoring {...props} subMenuRoute="Monitoring Faults" />}
                          />
                          <Route
                            path="/monitoring/fault-categories/"
                            render={props => <Monitoring {...props} subMenuRoute="Monitoring Fault Categories" />}
                          />
                          <Route
                            path="/tools/common-enumerations/"
                            render={props => (
                              <Tools {...props} subMenuRoute="Common Enumerations" />
                            )}
                          />
                          <Route
                            path="/tools/io-configuration/"
                            render={props => <Tools {...props} subMenuRoute="IO configuration" />}
                          />
                          <Route
                            path="/tools/open-binary-config-file/"
                            render={props => (
                              <Tools {...props} subMenuRoute="Open binary config file" />
                            )}
                          />
                          <Route
                            path="/tools/standard-boards/"
                            render={props => <Tools {...props} subMenuRoute="Standard Boards" />}
                          />
                          <Route
                            path="/tools/io-configuration/select-configuration"
                            render={props => (
                              <Tools {...props} subMenuRoute="Select Configuration" />
                            )}
                          />
                          <Route
                            path="/tools/development-package/"
                            render={props => (
                              <Tools {...props} subMenuRoute="Development Package" />
                            )}
                          />

                          {hasRole(activeUser, "license-generation") && (
                            <Route
                              path="/tools/license-data/"
                              render={props => <Tools {...props} subMenuRoute="License Data" />}
                            />
                          )}

                          {hasRole(activeUser, "license-upload") && (
                            <Route
                              path="/tools/license-upload/"
                              render={props => <Tools {...props} subMenuRoute="License Upload" />}
                            />
                          )}

                          <Route
                            path="/localization/status/"
                            render={props => <UnderConstruction {...props} />}
                          />

                          <Route
                            path="/localization/publish/"
                            render={props => (
                              <Localization
                                {...props}
                                subMenuRoute="Publish"
                                activeUser={activeUser}
                              />
                            )}
                          />

                          {isAdmin(activeUser) && (
                            <Route
                              path="/localization/languages/"
                              render={props => (
                                <Localization
                                  {...props}
                                  subMenuRoute="Languages"
                                  activeUser={activeUser}
                                />
                              )}
                            />
                          )}

                          {hasRole(activeUser, "read") && (
                            <Route
                              path="/localization/translationStatus/"
                              render={props => (
                                <Localization
                                  {...props}
                                  subMenuRoute="Status"
                                  activeUser={activeUser}
                                />
                              )}
                            />
                          )}

                          {hasRole(activeUser, "read") && (
                            <Route
                              path="/localization/translationLog/"
                              render={props => (
                                <Localization
                                  {...props}
                                  subMenuRoute="Translation Log"
                                  activeUser={activeUser}
                                />
                              )}
                            />
                          )}

                          {hasRole(activeUser, "translation") && (
                            <Route
                              path="/localization/translation/"
                              render={props => (
                                <Localization
                                  {...props}
                                  subMenuRoute="Translation"
                                  activeUser={activeUser}
                                />
                              )}
                            />
                          )}
                          {/* When Redirect is rendered it redirects to a different route */}
                          <Route
                            exact={true}
                            path="/elevator/"
                            render={() => <Redirect to="/elevator/parameters/" />}
                          />
                          <Route
                            exact={true}
                            path="/group/"
                            render={() => <Redirect to="/group/parameters/" />}
                          />
                          <Route
                            exact={true}
                            path="/apm/"
                            render={() => <Redirect to="/apm/parameters/" />}
                          />
                          <Route
                            exact={true}
                            path="/drive/"
                            render={() => <Redirect to="/drive/parameters/" />}
                          />
                          <Route
                            exact={true}
                            path="/faults/"
                            render={() => <Redirect to="/faults/drive-faults/" />}
                          />
                          <Route
                            exact={true}
                            path="/parameters/"
                            render={() => <Redirect to="/parameters/drive-parameters/" />}
                          />
                          <Route
                            exact={true}
                            path="/enumerations/"
                            render={() => <Redirect to="/enumerations/domains/" />}
                          />
                          <Route
                            exact={true}
                            path="/safety/"
                            render={() => <Redirect to="/safety/parameters/" />}
                          />
                          <Route
                            exact={true}
                            path="/escalator/"
                            render={() => <Redirect to="/escalator/parameters/" />}
                          />
                          <Route
                            exact={true}
                            path="/door/"
                            render={() => <Redirect to="/door/parameters/" />}
                          />
                          <Route
                            exact={true}
                            path="/nonkone/"
                            render={() => <Redirect to="/nonkone/faults/" />}
                          />
                          <Route
                            exact={true}
                            path="/signalization/"
                            render={() => <Redirect to="/signalization/faults/" />}/>
                            <Route
                            exact={true}
                            path="/network/"
                            render={() => <Redirect to="/network/faults/" />}
                          />
                          <Route
                            exact={true}
                            path="/monitoring/"
                            render={() => <Redirect to="/monitoring/faults/" />}
                          />
                          <Route
                            exact={true}
                            path="/tools/"
                            render={() => <Redirect to="/tools/io-configuration/" />}
                          />
                          <Route exact={true} path="/" render={() => <Redirect to="/home/" />} />
                          <Route
                            exact={true}
                            path="/users/"
                            render={() => <Redirect to="/users/users" />}
                          />
                          <Route
                            exact={true}
                            path="/users/"
                            render={() => <Redirect to="/users/users" />}
                          />
                          <Route
                            exact={true}
                            path="/localization/"
                            render={() => <Redirect to={"/localization/translationStatus"} />}
                          />
                          {isAdmin(activeUser) && (
                            <Route
                              exact={true}
                              path="/users/users"
                              render={props => <Users {...props} subMenuRoute="Users" />}
                            />
                          )}
                          {isAdmin(activeUser) && (
                            <Route
                              exact={true}
                              path="/users/aadgroups/"
                              render={props => <Users {...props} subMenuRoute="AADGroups" />}
                            />
                          )}
                          {/* If Switch component didn't find any other route it renders this last one. */}
                          <Route component={ErrorPage} />
                        </Switch>
                      </div>
                    </div>
                  </div>
                )}
              />
            </>
          )}
        </div>
      </Router>
    );
  }
  private handleReload = (): void => {
    this.props.pdApiActions.fetchData();
    this.props.pdApiActions.fetchAllOnClick();
  };
}

// This function is a part of Redux. It connects Redux state with React props. All props for common state need to be connected.
const mapStateToProps = (state: State.Root) => ({
  fetchingStatus: state.main.fetchingStatus,
  activeSubmenu: state.main.activeSubmenu,
  activeUser: state.main.activeUser
});

// This function is part of Redux. It connects Redux dispatch and actions with React props. All actions need to be connected.
const mapDispatchToProps = (dispatch: Dispatch<PdApiActions | MainActions | EditActions>) => ({
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch),
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

// Adds Redux functionality into the component by defining it as a higher-order component.
export default connect(mapStateToProps, mapDispatchToProps)(AuthProvider(Main));

